import { useEffect, useRef, useState } from "react";
import type { Region } from "@gooner/shared";
import { isSafeMode } from "../lib/safe-mode";
import { useTileOnscreen } from "./useTileOnscreen";
import { useAdaptiveFraming } from "./useAdaptiveFraming";
import type { SyncBus, SyncState } from "./syncBus";

/** How far out of step a follower may drift before it is nudged back, in seconds. */
const DRIFT_TOLERANCE = 0.12;
/** How often to check. Cheap — one property read per follower per tick. */
/** Matches the leader's, so a synced pair re-frames together rather than in a stagger. */
const FOLLOWER_MOVE_MS = 700;

const DRIFT_CHECK_MS = 1000;

/** The leader events after which a capture attempt is worth retrying. */
const READY_EVENTS = ["loadeddata", "playing", "canplay"] as const;

/** `captureStream` is still prefixed on Firefox and absent on Safari. */
type CapturableVideo = HTMLVideoElement & {
  captureStream?: () => MediaStream;
  mozCaptureStream?: () => MediaStream;
};

function captureFrom(el: HTMLVideoElement | null): MediaStream | null {
  const v = el as CapturableVideo | null;
  if (!v) return null;
  try {
    const stream = v.captureStream?.() ?? v.mozCaptureStream?.() ?? null;
    // A stream with no video track is a stream that will render black — worse than the
    // fallback, so treat it as unavailable rather than "captured".
    return stream && stream.getVideoTracks().length > 0 ? stream : null;
  } catch {
    // Capture throws on a tainted element (a cross-origin clip without CORS). Media is
    // same-origin through /api/media, but a source that starts proxying differently
    // should degrade to the fallback rather than take the tile down.
    return null;
  }
}

/**
 * A region that plays whatever another region is playing.
 *
 * It runs no feed: no clip selection, no buffer, no advance timer, no `onView` tally.
 * Everything comes from the leader over the bus, which is what makes two synced tiles one
 * playback rather than two that happen to agree.
 *
 * **Normal** is the same picture. **Flip** mirrors it across a vertical axis, so a pair
 * reads as one wide symmetrical image rather than as the same clip shown twice.
 *
 * ## Why this does not download the clip twice
 *
 * The obvious build — point a second `<video>` at the same url and let the HTTP cache
 * dedupe — does NOT work, and measuring is the only way to find that out. Media elements
 * fetch by Range, and two elements issue two independent 206 streams for the same url
 * even though the response is `immutable, max-age=31536000`. It was verified doing
 * exactly that: one clip, two requests.
 *
 * So the follower takes the leader's decoded output directly via `captureStream()`. One
 * download, one decode, and the frames are the SAME frames — there is no drift to correct
 * because there is nothing to keep in step.
 *
 * Where capture is unavailable (Safari), it falls back to the same-url element and keeps
 * it aligned by seeking. That costs the second fetch, which is the honest price of the
 * fallback rather than a claim quietly not being met.
 *
 * The clip is not counted twice either: `onView` belongs to the leader, and a synced pair
 * is one clip being watched. Counting both would inflate every view and fap statistic by
 * however many followers a wall happened to have.
 */
export function SyncedRegion({
  region,
  leaderId,
  bus,
  paused,
}: {
  region: Region;
  leaderId: string;
  bus: SyncBus;
  paused: boolean;
}) {
  const [state, setState] = useState<SyncState | null>(() => bus.get(leaderId));
  const [streaming, setStreaming] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [box, setBox] = useState<{ w: number; h: number } | null>(null);

  // A follower needs its own tile measurement for the same reason the leader does: the
  // crop depends on the tile's ASPECT, and a follower can be a different shape from the
  // region it follows. Same-value bailout by hand — see RegionPlayer's note.
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    const measure = () => {
      const next = { w: el.clientWidth, h: el.clientHeight };
      if (!next.w || !next.h) return;
      setBox((prev) => (prev && prev.w === next.w && prev.h === next.h ? prev : next));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [state?.item?.id]);
  /*
   * A follower off screen is a second decode nobody is watching.
   *
   * On the captured path the leader's frames arrive whether or not this element renders
   * them, so pausing here is what actually stops the work. On the fallback path it also
   * stops a second download. Either way the leader is unaffected — a follower going
   * quiet must never disturb what it follows.
   */
  const onscreen = useTileOnscreen(videoRef);
  const still = paused || !onscreen;

  useEffect(() => {
    // Seeded from `get` as well as subscribed, because a follower that mounts mid-clip
    // would otherwise sit black until the leader next advanced — up to a full clip of
    // nothing, and on a "full clip" timer that can be minutes.
    setState(bus.get(leaderId));
    return bus.subscribe(leaderId, setState);
  }, [bus, leaderId]);

  /*
   * Take the leader's decoded output, if this browser will give it to us.
   *
   * Re-captured whenever the leader changes CLIP, not just element: the leader crossfades
   * between two elements, so its live element changes under us, and a stream captured
   * from the outgoing one freezes on its last frame.
   */
  const leaderVideo = state?.video ?? null;
  // Through a ref: the leader crossfades between two elements, so a clock that captured
  // one would keep reading a element that has stopped advancing.
  const leaderVideoRef = useRef<HTMLVideoElement | null>(null);
  leaderVideoRef.current = leaderVideo;
  const leaderSrc = state?.src ?? null;
  useEffect(() => {
    const follower = videoRef.current;
    if (!follower) return;
    // No leader element (a leader whose source just changed announces before it mounts):
    // there is nothing to capture, so the follower must be on its own-url fallback. Left
    // `streaming` it renders with neither `src` nor `srcObject` — frozen on its last frame.
    if (!leaderVideo) {
      setStreaming(false);
      return;
    }
    let attached: MediaStream | null = null;

    const attach = (): boolean => {
      const stream = captureFrom(leaderVideo);
      if (!stream) return false;
      attached = stream;
      follower.srcObject = stream;
      setStreaming(true);
      if (!paused) void follower.play().catch(() => undefined);
      return true;
    };

    /*
     * A captured track DIES when its source element fires `ended` — and the leader's
     * element does exactly that on every clip (loop is off; `ended` IS the advance). When
     * the leader then replays itself as the gap bridge (RegionPlayer's onEnded), the
     * follower is still holding the dead stream and renders BLACK for the whole wait,
     * while the tile it follows keeps moving. So "is there a live stream attached" is
     * re-asked on every leader readiness event for the life of the attachment, and a
     * dead, muted or missing one is re-captured — a fresh captureStream() on the
     * restarted element carries a live track again. The same listener is the retry for
     * a first capture that found no data yet (see below); the two cases are one question.
     */
    const attachedIsLive = () =>
      attached !== null &&
      attached.getVideoTracks().some((t) => t.readyState === "live" && !t.muted);
    const ensureLive = () => {
      if (!attachedIsLive()) attach();
    };

    /*
     * Retried on the leader's own readiness events, not just attempted once.
     *
     * `captureStream()` on an element with no data yet returns a stream carrying zero
     * video tracks — not an error, just nothing to show. A single attempt therefore fails
     * silently whenever the leader is still loading, which is exactly when a busy wall is
     * transcoding renditions, and the follower falls back to fetching the clip a second
     * time — losing the bandwidth saving precisely when it is worth most. Measured that
     * happening before this was added.
     */
    if (!attach()) setStreaming(false);
    for (const ev of READY_EVENTS) leaderVideo.addEventListener(ev, ensureLive);
    return () => {
      for (const ev of READY_EVENTS) leaderVideo.removeEventListener(ev, ensureLive);
      // Released explicitly: a dangling srcObject keeps the leader's capture alive after
      // this tile is gone, which on a wall that reshapes often accumulates.
      if (attached && follower.srcObject === attached) follower.srcObject = null;
    };
    // `paused` deliberately absent — re-capturing on every pause would restart the
    // stream; the effect below handles play/pause on the existing one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leaderVideo, leaderSrc]);

  // Follow the wall's play/pause state, and stop entirely when off screen.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (still) v.pause();
    else void v.play().catch(() => undefined);
  }, [still, leaderSrc, streaming]);

  /*
   * Hold the follower to the leader's clock — fallback path only.
   *
   * On a captured stream there is nothing to correct: the follower is displaying the
   * leader's own frames. On the fallback, two elements handed the same url do NOT stay
   * together — each starts when its own first frame decodes, and a dropped frame or a
   * stalled read pushes them further apart over a long clip. Side by side that reads as
   * an echo.
   *
   * Corrected only past a tolerance, since seeking every tick stutters visibly and a
   * twelfth of a second is under what the eye catches on two tiles that are not adjacent.
   */
  useEffect(() => {
    // Nothing to keep in step while this tile is still.
    if (still || streaming) return;
    const id = setInterval(() => {
      const follower = videoRef.current;
      const leader = leaderVideo;
      if (!follower || !leader || leader.paused || follower.seeking) return;
      if (!Number.isFinite(leader.currentTime) || follower.readyState < 2) return;
      if (Math.abs(follower.currentTime - leader.currentTime) > DRIFT_TOLERANCE) {
        follower.currentTime = leader.currentTime;
      }
    }, DRIFT_CHECK_MS);
    return () => clearInterval(id);
  }, [leaderVideo, still, streaming]);

  const fit = region.fit === "contain" ? "object-contain" : "object-cover";
  const mirrored = region.syncMode === "flip";

  /*
   * A follower is framed too, or a synced pair reads as broken: the leader pans to hold
   * the subject and the tile beside it sits dead centre on the same clip.
   *
   * Its clock is the leader's whenever the stream is captured — this element's own
   * currentTime is meaningless against a live MediaStream — and its own otherwise, where
   * `loop` is on and the position genuinely wraps.
   */
  useAdaptiveFraming(
    [
      {
        ref: videoRef,
        item: state?.item ?? null,
        mirrored,
        clock: streaming ? () => leaderVideoRef.current?.currentTime ?? 0 : undefined,
      },
    ],
    region,
    box,
    FOLLOWER_MOVE_MS,
  );

  if (!state) {
    // The leader is gone, or has not started. Black rather than a spinner: this tile has
    // nothing of its own to load, so a spinner would promise activity that is not
    // happening here.
    return <div className="absolute inset-0 bg-black" />;
  }

  const common = `absolute inset-0 h-full w-full ${fit}`;
  // scaleX, not scaleY: reflecting ACROSS a vertical axis. scaleY would stand the clip on
  // its head, which is a different effect and not one anybody asked for.
  // For the <img> branch only. The VIDEO's transform belongs to useAdaptiveFraming,
  // which composes the reflection into the framing — setting it here too would mean React
  // re-applying a mirror-only value on every render and wiping the framing with it, which
  // is exactly what a resize does.
  const transform = mirrored ? "scaleX(-1)" : undefined;

  /*
   * The tile CLIPS. Adaptive Framing zooms by scaling the element, so without this the
   * element simply renders bigger than its region and spills over its neighbours — the
   * tile appears to change size. RegionPlayer has always had this wrapper; a follower
   * never needed one until it could zoom.
   */
  return (
    <div className="absolute inset-0 overflow-hidden">
      {state.item.kind === "video" ? (
    <video
      ref={videoRef}
      className={common}
      // No `src` while streaming: setting both makes the element pick one, and which one
      // is not something to leave to chance.
      src={streaming ? undefined : state.src}
      poster={state.item.posterUrl}
      autoPlay
      playsInline
      loop={!streaming}
      // Always silent. Two elements playing the same audio milliseconds apart is a
      // flanging echo, and the leader is already carrying the sound.
      muted
      controls={false}
      disablePictureInPicture={isSafeMode()}
      onCanPlay={(e) => {
        const v = e.currentTarget;
        // Fallback path only: land on the leader's position immediately rather than
        // waiting for the drift check, so a follower mounting mid-clip never replays the
        // opening. A captured stream is already at the leader's position by definition.
        if (!streaming && leaderVideo && Number.isFinite(leaderVideo.currentTime)) {
          v.currentTime = leaderVideo.currentTime;
        }
        if (!still) void v.play().catch(() => undefined);
      }}
    />
      ) : (
        <img className={common} style={{ transform }} src={state.src} alt="" draggable={false} />
      )}
    </div>
  );
}
